import type { Dispatch, SetStateAction } from 'react';
import Card from '@components/UI/Card';
import Button from '@components/UI/Button';
import { DeleteButton, EditButton, EmptyState } from '@components/UI';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { PriceAdjustmentRule, TradingProfile } from '@einhaus/project314-types';
import type { NewLimitPriceAdjustmentRuleState, SaveTradingProfileSettings } from './types';

interface LimitPriceAdjustmentRulesSectionProps {
    priceAdjustmentRules: PriceAdjustmentRule[];
    setPriceAdjustmentRules: Dispatch<SetStateAction<PriceAdjustmentRule[]>>;
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    tradingProfileId: number;
    savePriceAdjustmentRule: SaveTradingProfileSettings;
    setEditingLimitPriceAdjustmentRuleId: Dispatch<SetStateAction<string | null>>;
    setNewLimitPriceAdjustmentRule: Dispatch<SetStateAction<NewLimitPriceAdjustmentRuleState>>;
    setShowLimitPriceAdjustmentRulesModal: Dispatch<SetStateAction<boolean>>;
}

const LimitPriceAdjustmentRulesSection = ({
    priceAdjustmentRules,
    setPriceAdjustmentRules,
    formData,
    setFormData,
    tradingProfileId,
    savePriceAdjustmentRule,
    setEditingLimitPriceAdjustmentRuleId,
    setNewLimitPriceAdjustmentRule,
    setShowLimitPriceAdjustmentRulesModal
}: LimitPriceAdjustmentRulesSectionProps) => {
    return (
        <Card className="mb-4" headerClassName="flex justify-between items-center" title="Limit Price Adjustment Rules">
            <Button
                variant="outline-dark"
                onClick={() => {
                    setEditingLimitPriceAdjustmentRuleId(null);

                    setNewLimitPriceAdjustmentRule({
                        tradeType: '',
                        direction: '',
                        percentage: null,
                        minutes: null,
                        priceReference: 'currentLimit',
                        adjustmentUnit: 'percentage',
                        adjustmentValue: null
                    });

                    setShowLimitPriceAdjustmentRulesModal(true);
                }}
            >
                + Add
            </Button>

            {priceAdjustmentRules.length === 0 ? (
                <EmptyState size="sm" title="No limit price adjustment rules added yet." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="table-auto w-full text-white border-collapse">
                        <thead>
                            <tr>
                                <th className="px-4 py-2 text-left">Trade Type</th>
                                <th className="px-4 py-2 text-left">Reference</th>
                                <th className="px-4 py-2 text-left">Direction</th>
                                <th className="px-4 py-2 text-left">Adjustment</th>
                                <th className="px-4 py-2 text-left">Minutes</th>
                                <th className="px-4 py-2 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {priceAdjustmentRules.map((rule, index) => (
                                <tr
                                    className={`border-b border-zinc-700 hover:bg-zinc-700 ${
                                        index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'
                                    }`}
                                    key={rule.id}
                                >
                                    <td className="px-4 py-2">{rule.tradeType === 'buy' ? 'Buy' : 'Sell'}</td>
                                    <td className="px-4 py-2">{rule.priceReference ?? 'currentLimit'}</td>
                                    <td className="px-4 py-2">{rule.direction === 'up' ? 'Up' : 'Down'}</td>
                                    <td className="px-4 py-2">
                                        {rule.adjustmentUnit === 'ticks'
                                            ? `${rule.adjustmentValue ?? rule.percentage} tick(s)`
                                            : `${rule.adjustmentValue ?? rule.percentage}%`}
                                    </td>
                                    <td className="px-4 py-2">{rule.minutes}</td>
                                    <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            <EditButton
                                                size="sm"
                                                onClick={() => {
                                                    setEditingLimitPriceAdjustmentRuleId(rule.id);

                                                    setNewLimitPriceAdjustmentRule({
                                                        tradeType: rule.tradeType,
                                                        direction: rule.direction,
                                                        percentage: rule.percentage,
                                                        minutes: rule.minutes,
                                                        priceReference: rule.priceReference ?? 'currentLimit',
                                                        adjustmentUnit: rule.adjustmentUnit ?? 'percentage',
                                                        adjustmentValue: rule.adjustmentValue ?? rule.percentage
                                                    });

                                                    setShowLimitPriceAdjustmentRulesModal(true);
                                                }}
                                            />
                                            <DeleteButton
                                                size="sm"
                                                onClick={() => {
                                                    const updatedRules = priceAdjustmentRules.filter((r) => r.id !== rule.id);
                                                    setPriceAdjustmentRules(updatedRules);

                                                    // Update the form data with the price adjustment rule removed
                                                    const updatedFormData = {
                                                        ...formData,
                                                        brokerageOrderRules: {
                                                            ...formData.brokerageOrderRules,
                                                            priceAdjustmentRules: updatedRules
                                                        }
                                                    };

                                                    setFormData(updatedFormData);
                                                    showSpinner();

                                                    // Use the price adjustment rule mutation to save the updated settings
                                                    savePriceAdjustmentRule(
                                                        {
                                                            tradingProfileId,
                                                            settings: updatedFormData
                                                        },
                                                        {
                                                            onSuccess: () => {
                                                                hideSpinner();
                                                            },
                                                            onError: () => {
                                                                hideSpinner();
                                                            }
                                                        }
                                                    );
                                                }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};

export default LimitPriceAdjustmentRulesSection;
